import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Zap, Shield, Clock, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MarketingHeader } from "@/components/MarketingHeader";
import { MarketingFooter } from "@/components/MarketingFooter";
import { MarketingBackground } from "@/components/MarketingBackground";
import { BackgroundShapes } from "@/components/BackgroundShapes";
import { SEO } from "@/components/SEO";

const Pricing = () => {
  const navigate = useNavigate();

  const priceSchema = {
    "@context": "https://schema.org",
    "@type": "Offer",
    "name": "Vocal Coaching Sessions",
    "description": "Professional vocal coaching sessions from 15 to 90 minutes",
    "price": "1.99",
    "priceCurrency": "USD",
    "priceValidUntil": "2026-12-31",
    "availability": "https://schema.org/InStock",
    "url": "https://vocalzing.lovable.app/pricing"
  };

  const pricingTiers = [
    {
      duration: "15 min",
      price: "$29.85",
      description: "Quick warm-up or technique check",
      popular: false
    },
    {
      duration: "30 min",
      price: "$59.70",
      description: "Focused practice session",
      popular: false
    },
    {
      duration: "45 min",
      price: "$89.55",
      description: "Standard lesson length",
      popular: true
    },
    {
      duration: "60 min",
      price: "$119.40",
      description: "Comprehensive training",
      popular: false
    },
    {
      duration: "90 min",
      price: "$179.10",
      description: "Extended deep-dive session",
      popular: false
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: "Instant Booking",
      description: "Book your lesson in seconds and start learning immediately"
    },
    {
      icon: Shield,
      title: "Secure Payments",
      description: "All transactions are protected with industry-standard encryption"
    },
    {
      icon: Clock,
      title: "Flexible Duration",
      description: "Choose any length from 15 to 90 minutes that fits your schedule"
    },
    {
      icon: TrendingUp,
      title: "Progress Tracking",
      description: "Monitor your improvement with detailed analytics included"
    }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden">
      <MarketingBackground />
      <div className="pt-[73px]">
        <SEO
        title="Pricing - Affordable Vocal Lessons from $29.85"
        description="Simple, transparent pricing for vocal coaching. Pay $1.99/minute for flexible sessions from 15 to 90 minutes. No subscriptions, no hidden fees. Book your lesson today."
        keywords="vocal lesson pricing, singing lesson cost, voice coaching rates, affordable vocal training, online singing lessons price"
        canonical="https://vocalzing.lovable.app/pricing"
        jsonLd={priceSchema}
      />
      <MarketingHeader />
      
      {/* Hero Section */}
      <section className="relative py-24 px-4 overflow-hidden" aria-label="Pricing hero section">
        <BackgroundShapes variant="pricing" />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="container mx-auto text-center relative z-10 max-w-4xl">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">
            Simple, Transparent{" "}
            <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Pricing
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground mb-8 animate-slide-up">
            Pay only for the time you need. No subscriptions, no commitments, no hidden fees.
          </p>
          <div className="inline-flex items-center gap-3 bg-primary/10 text-primary px-6 py-3 rounded-full">
            <span className="text-3xl font-bold">$1.99</span>
            <span className="text-lg">per minute</span>
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-20 px-4 relative">
        <BackgroundShapes variant="features" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Choose Your Session Length</h2>
            <p className="text-lg text-muted-foreground">All lessons include the same great features</p>
          </div>
          
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {pricingTiers.map((tier, index) => (
              <Card 
                key={index} 
                className={`relative hover-lift transition-all ${
                  tier.popular 
                    ? 'border-primary shadow-xl scale-105 bg-gradient-to-b from-primary/5 to-transparent' 
                    : 'border-border/40 bg-card/50 backdrop-blur'
                }`}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-2xl mb-2">{tier.duration}</CardTitle>
                  <div className="text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2">
                    {tier.price}
                  </div>
                  <CardDescription className="text-sm">{tier.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    className="w-full"
                    variant={tier.popular ? "default" : "outline"}
                    onClick={() => navigate("/booking")}
                  >
                    Book Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-muted/30 relative">
        <BackgroundShapes variant="testimonials" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What's Included</h2>
            <p className="text-lg text-muted-foreground">Every session comes with these benefits</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="border-border/40 hover-lift bg-card/50 backdrop-blur text-center">
                  <CardHeader>
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    <CardTitle className="text-lg mb-2">{benefit.title}</CardTitle>
                    <CardDescription className="text-sm leading-relaxed">
                      {benefit.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>

          <div className="mt-16 max-w-3xl mx-auto">
            <Card className="border-2 border-primary/30 bg-card/50 backdrop-blur">
              <CardHeader>
                <CardTitle className="text-center text-xl">Every Lesson Includes:</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="grid md:grid-cols-2 gap-4">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>One-on-one expert coaching</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Integrated video conferencing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Personalized lesson plans</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Progress tracking & analytics</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Flexible scheduling</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Secure payment processing</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-muted-foreground">Everything you need to know about our pricing</p>
          </div>
          
          <div className="space-y-6">
            <Card className="border-border/40 hover-lift bg-card/50 backdrop-blur">
              <CardHeader>
                <CardTitle className="text-xl">How does payment work?</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base leading-relaxed text-foreground">
                  Your payment is securely authorized when you book. The charge is only processed after your coach accepts the booking. 
                  If your booking is denied by all coaches, you're never charged.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-border/40 hover-lift bg-card/50 backdrop-blur">
              <CardHeader>
                <CardTitle className="text-xl">Can I cancel or reschedule?</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base leading-relaxed text-foreground">
                  You can request to cancel or reschedule before your coach accepts the booking. Once accepted, please contact 
                  your coach directly to discuss rescheduling. Our cancellation policy includes a $15 no-show fee if cancelled late.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-border/40 hover-lift bg-card/50 backdrop-blur">
              <CardHeader>
                <CardTitle className="text-xl">Are there any additional fees?</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base leading-relaxed text-foreground">
                  No hidden fees! The price you see ($1.99/minute) is what you pay. The only additional charge is a $15 
                  cancellation fee if you cancel a confirmed lesson without proper notice. All payments are securely processed through Stripe.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-border/40 hover-lift bg-card/50 backdrop-blur">
              <CardHeader>
                <CardTitle className="text-xl">What payment methods do you accept?</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base leading-relaxed text-foreground">
                  We accept all major credit cards (Visa, Mastercard, American Express), debit cards, and other payment 
                  methods supported by Stripe for secure and reliable payment processing.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
            Join hundreds of students improving their vocal skills with personalized coaching
          </p>
          <Button 
            size="lg" 
            className="text-lg px-12 py-6"
            onClick={() => navigate("/booking")}
          >
            Book Your First Lesson
          </Button>
        </div>
      </section>

      <MarketingFooter />
      </div>
    </div>
  );
};

export default Pricing;
